import { useState, type FormEvent } from 'react';
import {
  Box, Typography, Paper, Stack, Button, TextField, Switch, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
} from '@mui/material';
import { AlarmOutlined, DeleteOutlineOutlined, EditOutlined, AddOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MedSchedule } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';

const DEFAULT_TZ = 'Australia/Melbourne';

export default function Reminders() {
  const [editing, setEditing] = useState<MedSchedule | 'new' | null>(null);
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['med-schedules'],
    queryFn: () => api.get<MedSchedule[]>('/api/med-schedules'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<MedSchedule>(`/api/med-schedules/${id}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['med-schedules'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/med-schedules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['med-schedules'] }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Med reminders</Typography>
        <Button
          variant="contained"
          startIcon={<AddOutlined />}
          onClick={() => setEditing('new')}
        >
          Add reminder
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        {(listQuery.data ?? []).length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            No reminders set up yet.
          </Box>
        )}
        {(listQuery.data ?? []).map((s) => (
          <Paper key={s.id} variant="outlined" sx={{ p: 2, opacity: s.enabled ? 1 : 0.55 }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <AlarmOutlined color={s.enabled ? 'primary' : 'disabled'} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {s.medName}
                  {s.medDose && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      {s.medDose}
                    </Typography>
                  )}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                  <Chip size="small" label={`${s.timeLocal} ${s.timezone.replace('Australia/', '')}`} />
                  <Typography variant="caption" color="text.secondary">
                    Alert {s.leadMinutes} min before
                    {s.lastFiredForDate && ` · last fired ${s.lastFiredForDate}`}
                    {' · by '}
                    {s.createdByName}
                  </Typography>
                </Stack>
              </Box>
              <Switch
                checked={s.enabled}
                onChange={(_, checked) => toggleMutation.mutate({ id: s.id, enabled: checked })}
                disabled={toggleMutation.isPending}
              />
              <IconButton onClick={() => setEditing(s)}>
                <EditOutlined />
              </IconButton>
              <IconButton
                onClick={() => {
                  if (confirm(`Delete reminder for ${s.medName}?`)) deleteMutation.mutate(s.id);
                }}
                disabled={deleteMutation.isPending}
              >
                <DeleteOutlineOutlined />
              </IconButton>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <ReminderDialog
        open={editing !== null}
        schedule={editing === 'new' ? null : (editing ?? null)}
        onClose={() => setEditing(null)}
      />
    </Box>
  );
}

function ReminderDialog({
  open, schedule, onClose,
}: {
  open: boolean;
  schedule: MedSchedule | null;
  onClose: () => void;
}) {
  const isNew = schedule === null;
  const [medName, setMedName] = useState(schedule?.medName ?? '');
  const [medDose, setMedDose] = useState(schedule?.medDose ?? '');
  const [timeLocal, setTimeLocal] = useState(schedule?.timeLocal ?? '08:00');
  const [timezone, setTimezone] = useState(schedule?.timezone ?? DEFAULT_TZ);
  const [leadMinutes, setLeadMinutes] = useState(schedule?.leadMinutes ?? 10);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        medName: medName.trim(),
        medDose: medDose.trim() || null,
        timeLocal,
        timezone,
        leadMinutes,
      };
      if (isNew) return api.post<MedSchedule>('/api/med-schedules', payload);
      return api.patch<MedSchedule>(`/api/med-schedules/${schedule!.id}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['med-schedules'] });
      onClose();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!medName.trim() || !/^\d{2}:\d{2}$/.test(timeLocal)) return;
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isNew ? 'New reminder' : 'Edit reminder'}</DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Medication"
              value={medName}
              onChange={(e) => setMedName(e.target.value)}
              required
              autoFocus
              size="small"
            />
            <TextField
              label="Dose (optional)"
              value={medDose}
              onChange={(e) => setMedDose(e.target.value)}
              size="small"
              placeholder="e.g. 5mg"
            />
            <TextField
              label="Time"
              type="time"
              value={timeLocal}
              onChange={(e) => setTimeLocal(e.target.value)}
              required
              size="small"
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 60 } }}
            />
            <TextField
              label="Timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              size="small"
              helperText="IANA timezone, e.g. Australia/Melbourne"
            />
            <TextField
              label="Alert minutes before"
              type="number"
              value={leadMinutes}
              onChange={(e) => setLeadMinutes(Math.max(0, Math.min(240, Number(e.target.value))))}
              size="small"
            />
            {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending || !medName.trim()}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
